import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { ReactElement } from "react";
import {
  AttachmentsPanel,
  CepInput,
  CustomFieldsPanel,
  DocInput,
  Field,
  focusFirstInvalid,
  FormPage,
  FormSection,
  MoneyInput,
  QueryStatus,
  Tabs,
  useToast,
} from "@cx/ui";
import type {
  AttachmentRow,
  ContactDetail,
  ContactFormValues,
  ContactInput,
  CustomFieldRow,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { LOOKUP_CNPJ, lookupCepPath } from "../../endpoints";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { EMPTY, toFormValues, useContactForm, type Addr, type AddrPatch } from "./useContactForm";
import { canManageSellerType, keepSellerType } from "./seller-type";

/** Resposta de GET /cep/:cep (Task 9, `apps/api/src/lookup/cep.ts`). */
type CepData = {
  cep: string;
  street: string | null;
  district: string | null;
  city: string;
  uf: string;
  cityIbge: string | null;
};
/** Resposta de POST /contacts/lookup (Task 9, `apps/api/src/lookup/cnpj.ts`); `address` tem os mesmos campos do endereço do formulário. */
type CnpjData = {
  name: string;
  tradeName: string | null;
  crt: number | null;
  email: string | null;
  phone: string | null;
  address: AddrPatch;
};
const UFS = "AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO".split(" ");

/**
 * Rota do formulário. Espera o detalhe buscado NESTA montagem (nunca semeia com cache velho) e remonta o formulário
 * a cada id. Refetch em segundo plano não sobrescreve o que está sendo digitado, porque o estado só é semeado na montagem (FE16).
 */
export function ContactFormPage(): ReactElement {
  const { id } = useParams({ strict: false });
  const { companyId, api } = useSession();
  const detail = useQuery({
    queryKey: ["contact", companyId, id],
    enabled: !!id,
    // Sempre busca ao montar: com o cache ainda "fresco" (staleTime) não haveria fetch e isFetchedAfterMount
    // ficaria falso para sempre, deixando a tela em "Carregando…" (FE16).
    refetchOnMount: "always",
    queryFn: () => api<ContactDetail>(`/contacts/${id}`),
  });
  if (id && detail.isError && !detail.data) return <QueryStatus error={detail.error} />;
  if (id && !detail.isFetchedAfterMount) return <QueryStatus loading />;
  return (
    <ContactForm key={id ?? "novo"} id={id} initial={id && detail.data ? toFormValues(detail.data) : EMPTY} />
  );
}

function ContactForm({ id, initial }: { id?: string; initial: ContactFormValues }) {
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const f = useContactForm(initial);
  const v = f.values;
  const main: Addr = v.addresses?.find((a) => a.kind === "main") ?? { kind: "main", country: "BR" };

  const types = useQuery({
    queryKey: ["contact-types", companyId],
    queryFn: () => api<{ data: { id: string; name: string }[] }>("/contacts/types"),
  });
  // Marcar/desmarcar "Vendedor" cria/exclui o vendedor: só com sellers:create e sellers:delete.
  const canManageSellers = canManageSellerType((a) => can(perms, "sellers", a));
  const sellerTypeId = types.data?.data.find((t) => t.name === "Vendedor")?.id;
  // Vendedores ativos (o atual continua na lista mesmo se ficou inativo, para não sumir do campo).
  const sellerOptions = useQuery({
    queryKey: ["sellers", companyId, "options"],
    queryFn: () => api<{ data: { id: string; name: string }[] }>("/sellers/options"),
  });
  const fields = useQuery({
    queryKey: ["custom-fields", companyId, "contacts"],
    queryFn: () => api<{ data: CustomFieldRow[] }>("/custom-fields?module=contacts"),
  });
  const files = useQuery({
    queryKey: ["attachments", companyId, id],
    enabled: !isNew,
    queryFn: () => api<{ data: AttachmentRow[] }>(`/attachments?entityType=contact&entityId=${id}`),
  });

  const save = useMutation({
    mutationFn: (payload: ContactInput) =>
      api<{ id?: string; warnings: string[] }>(isNew ? "/contacts" : `/contacts/${id}`, {
        method: isNew ? "POST" : "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: (r) => {
      toast("success", "Contato salvo");
      if (r.warnings.includes("document_duplicate"))
        toast("warning", "Já existe outro contato com este documento estrangeiro.");
      f.markSaved();
      const savedId = r.id ?? id;
      qc.invalidateQueries({ queryKey: ["contacts", companyId] });
      // Marcar ou desmarcar o tipo "Vendedor" cria ou exclui o vendedor.
      qc.invalidateQueries({ queryKey: ["sellers", companyId] });
      if (savedId) qc.invalidateQueries({ queryKey: ["contact", companyId, savedId] });
      if (isNew && r.id) nav({ to: "/cadastros/contatos/$id", params: { id: r.id }, replace: true });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) f.setErrors(e.fields);
      // CPF/CNPJ duplicado na empresa bloqueia com 409 duplicate_document (decisão 10): a mensagem da API vai para o campo do documento.
      else if (e instanceof ApiFailure && e.code === "duplicate_document")
        f.setErrors({ document: e.message });
      toast("danger", errorMessage(e));
      focusFirstInvalid();
    },
  });
  const onSave = () => {
    const p = f.validate();
    if (p) return save.mutate(p);
    toast("danger", "Verifique os campos destacados.");
    // FormSection/Tabs abrem o que tem erro; o foco vai para o primeiro campo inválido (FE11).
    focusFirstInvalid();
  };

  const lookupCnpj = async (doc: string) => {
    try {
      const d = await api<CnpjData>(LOOKUP_CNPJ, { method: "POST", body: JSON.stringify({ document: doc }) });
      f.setValues((x) => ({
        ...x,
        name: x.name || d.name,
        tradeName: x.tradeName || d.tradeName,
        email: x.email || d.email,
        phone: x.phone || d.phone,
        crt: d.crt ?? x.crt,
      }));
      f.setAddr("main", d.address);
      toast("success", "Dados preenchidos pela Receita.");
    } catch (e) {
      toast("warning", errorMessage(e));
    }
  };
  const lookupCep = async (cep: string) => {
    try {
      const d = await api<CepData>(lookupCepPath(cep));
      f.setAddr("main", {
        cep: d.cep,
        street: d.street,
        district: d.district,
        city: d.city,
        uf: d.uf,
        cityIbge: d.cityIbge,
      });
    } catch (e) {
      toast("warning", errorMessage(e));
    }
  };
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("entityType", "contact");
      fd.append("entityId", id!);
      fd.append("file", file);
      return api("/attachments", { method: "POST", body: fd });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments", companyId, id] }),
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const delFile = useMutation({
    mutationFn: (fid: string) => api(`/attachments/${fid}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attachments", companyId, id] });
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });

  const err = f.errors;
  const docLabel = v.personType === "F" ? "CPF" : v.personType === "J" ? "CNPJ" : "Documento";
  const setPerson = (i: number, patch: Partial<NonNullable<ContactFormValues["persons"]>[number]>) =>
    f.set(
      "persons",
      (v.persons ?? []).map((x, j) => (j === i ? { ...x, ...patch } : x)),
    );

  return (
    <FormPage
      title={isNew ? "Novo contato" : v.name || "Contato"}
      breadcrumb={["Cadastros", "Clientes e Fornecedores", isNew ? "Incluir" : "Editar"]}
      onSave={onSave}
      onCancel={() => nav({ to: "/cadastros/contatos" })}
      saving={save.isPending}
      dirty={f.dirty}
    >
      {id && (can(perms, "receivables", "read") || can(perms, "payables", "read")) && (
        <p className="mb-2">
          <Link
            to="/financas/ficha-financeira/$id"
            params={{ id }}
            search={{ side: can(perms, "receivables", "read") ? "R" : "P", status: "all" }}
          >
            Ver ficha financeira
          </Link>
        </p>
      )}
      <Tabs
        tabs={[
          {
            key: "dados",
            label: "Dados",
            content: (
              <>
                <FormSection title="Dados cadastrais">
                  <Field label="Nome" required error={err.name} className="col-md-8">
                    <input value={v.name} onChange={(e) => f.set("name", e.target.value)} maxLength={120} />
                  </Field>
                  <Field label="Código" error={err.code} className="col-md-4">
                    <input value={v.code ?? ""} onChange={(e) => f.set("code", e.target.value)} />
                  </Field>
                  <Field label="Fantasia" error={err.tradeName} className="col-md-6">
                    <input value={v.tradeName ?? ""} onChange={(e) => f.set("tradeName", e.target.value)} />
                  </Field>
                  <Field label="Tipo da pessoa" required className="col-md-3">
                    <select
                      className="form-select"
                      value={v.personType}
                      onChange={(e) => f.set("personType", e.target.value as ContactFormValues["personType"])}
                    >
                      <option value="J">Jurídica</option>
                      <option value="F">Física</option>
                      <option value="E">Estrangeiro</option>
                    </select>
                  </Field>
                  <Field label={docLabel} error={err.document} className="col-md-3">
                    <DocInput
                      personType={v.personType}
                      value={v.document ?? ""}
                      onChange={(d) => f.set("document", d)}
                      onLookup={lookupCnpj}
                    />
                  </Field>
                  <Field label="Contribuinte" className="col-md-4">
                    <select
                      className="form-select"
                      value={v.ieIndicator}
                      onChange={(e) => f.set("ieIndicator", Number(e.target.value) as 1 | 2 | 9)}
                    >
                      <option value={1}>1 - Contribuinte ICMS</option>
                      <option value={2}>2 - Contribuinte isento</option>
                      <option value={9}>9 - Não contribuinte</option>
                    </select>
                  </Field>
                  <Field
                    label="Inscrição Estadual"
                    required={v.ieIndicator === 1}
                    error={err.ie}
                    className="col-md-4"
                  >
                    <input value={v.ie ?? ""} onChange={(e) => f.set("ie", e.target.value)} />
                  </Field>
                  <Field label="Cliente desde" error={err.customerSince} className="col-md-4">
                    <input
                      type="date"
                      value={v.customerSince ?? ""}
                      onChange={(e) => f.set("customerSince", e.target.value || null)}
                    />
                  </Field>
                  <Field label="Tipos de contato" className="col-md-8">
                    <select
                      multiple
                      className="form-select"
                      value={v.typeIds ?? []}
                      onChange={(e) =>
                        f.set(
                          "typeIds",
                          keepSellerType(
                            [...e.target.selectedOptions].map((o) => o.value),
                            v.typeIds ?? [],
                            sellerTypeId,
                            canManageSellers,
                          ),
                        )
                      }
                    >
                      {types.data?.data.map((t) => (
                        <option
                          key={t.id}
                          value={t.id}
                          disabled={t.id === sellerTypeId && !canManageSellers}
                          title={
                            t.id === sellerTypeId && !canManageSellers
                              ? "Você não tem permissão para gerenciar vendedores."
                              : undefined
                          }
                        >
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Situação" className="col-md-4">
                    <select
                      className="form-select"
                      value={v.status}
                      onChange={(e) => f.set("status", e.target.value as ContactFormValues["status"])}
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                      <option value="no_movement">Sem movimento</option>
                    </select>
                  </Field>
                  <Field label="Vendedor" error={err.sellerId} className="col-md-6">
                    <select
                      className="form-select"
                      value={v.sellerId ?? ""}
                      onChange={(e) => f.set("sellerId", e.target.value || null)}
                    >
                      <option value="">Nenhum</option>
                      {v.sellerId && !sellerOptions.data?.data.some((o) => o.id === v.sellerId) && (
                        <option value={v.sellerId}>Vendedor atual (inativo)</option>
                      )}
                      {sellerOptions.data?.data.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </FormSection>
                <FormSection title="Endereço">
                  <Field label="CEP" error={err["addresses.0.cep"]} className="col-md-3">
                    <CepInput
                      value={main.cep ?? ""}
                      onChange={(c) => f.setAddr("main", { cep: c })}
                      onResolved={lookupCep}
                    />
                  </Field>
                  <Field label="Endereço" className="col-md-6">
                    <input
                      value={main.street ?? ""}
                      onChange={(e) => f.setAddr("main", { street: e.target.value })}
                    />
                  </Field>
                  <Field label="Número" className="col-md-3">
                    <input
                      value={main.number ?? ""}
                      onChange={(e) => f.setAddr("main", { number: e.target.value })}
                    />
                  </Field>
                  <Field label="Complemento" className="col-md-4">
                    <input
                      value={main.complement ?? ""}
                      onChange={(e) => f.setAddr("main", { complement: e.target.value })}
                    />
                  </Field>
                  <Field label="Bairro" className="col-md-4">
                    <input
                      value={main.district ?? ""}
                      onChange={(e) => f.setAddr("main", { district: e.target.value })}
                    />
                  </Field>
                  <Field label="Cidade" className="col-md-3">
                    <input
                      value={main.city ?? ""}
                      onChange={(e) => f.setAddr("main", { city: e.target.value })}
                    />
                  </Field>
                  <Field label="UF" className="col-md-1">
                    <select
                      className="form-select"
                      value={main.uf ?? ""}
                      onChange={(e) => f.setAddr("main", { uf: e.target.value || null })}
                    >
                      <option value="">UF</option>
                      {UFS.map((u) => (
                        <option key={u}>{u}</option>
                      ))}
                    </select>
                  </Field>
                </FormSection>
                <FormSection title="Contato">
                  <Field label="Telefone" className="col-md-4">
                    <input
                      type="tel"
                      value={v.phone ?? ""}
                      onChange={(e) => f.set("phone", e.target.value)}
                    />
                  </Field>
                  <Field label="Celular" className="col-md-4">
                    <input
                      type="tel"
                      value={v.mobile ?? ""}
                      onChange={(e) => f.set("mobile", e.target.value)}
                    />
                  </Field>
                  <Field label="E-mail" error={err.email} className="col-md-4">
                    <input
                      type="email"
                      value={v.email ?? ""}
                      onChange={(e) => f.set("email", e.target.value)}
                    />
                  </Field>
                  <Field label="E-mail para envio da NF-e" error={err.nfeEmail} className="col-md-6">
                    <input
                      type="email"
                      value={v.nfeEmail ?? ""}
                      onChange={(e) => f.set("nfeEmail", e.target.value)}
                    />
                  </Field>
                  <Field label="Website" error={err.website} className="col-md-6">
                    <input
                      type="url"
                      value={v.website ?? ""}
                      onChange={(e) => f.set("website", e.target.value)}
                    />
                  </Field>
                </FormSection>
                <FormSection title="Dados adicionais" defaultOpen={false}>
                  <Field label="Estado civil" className="col-md-3">
                    <input
                      value={v.maritalStatus ?? ""}
                      onChange={(e) => f.set("maritalStatus", e.target.value)}
                    />
                  </Field>
                  <Field label="Profissão" className="col-md-3">
                    <input value={v.profession ?? ""} onChange={(e) => f.set("profession", e.target.value)} />
                  </Field>
                  <Field label="Sexo" className="col-md-2">
                    <select
                      className="form-select"
                      value={v.gender ?? ""}
                      onChange={(e) =>
                        f.set("gender", (e.target.value || null) as ContactFormValues["gender"])
                      }
                    >
                      <option value="">Não informado</option>
                      <option value="F">Feminino</option>
                      <option value="M">Masculino</option>
                      <option value="O">Outro</option>
                    </select>
                  </Field>
                  <Field label="Nascimento" error={err.birthDate} className="col-md-2">
                    <input
                      type="date"
                      value={v.birthDate ?? ""}
                      onChange={(e) => f.set("birthDate", e.target.value || null)}
                    />
                  </Field>
                  <Field label="Naturalidade" className="col-md-2">
                    <input value={v.birthplace ?? ""} onChange={(e) => f.set("birthplace", e.target.value)} />
                  </Field>
                  <Field label="Nome do pai" className="col-md-4">
                    <input value={v.fatherName ?? ""} onChange={(e) => f.set("fatherName", e.target.value)} />
                  </Field>
                  <Field label="CPF do pai" error={err.fatherCpf} className="col-md-2">
                    <input
                      value={v.fatherCpf ?? ""}
                      onChange={(e) => f.set("fatherCpf", e.target.value || null)}
                    />
                  </Field>
                  <Field label="Nome da mãe" className="col-md-4">
                    <input value={v.motherName ?? ""} onChange={(e) => f.set("motherName", e.target.value)} />
                  </Field>
                  <Field label="CPF da mãe" error={err.motherCpf} className="col-md-2">
                    <input
                      value={v.motherCpf ?? ""}
                      onChange={(e) => f.set("motherCpf", e.target.value || null)}
                    />
                  </Field>
                  <Field label="RG" className="col-md-3">
                    <input value={v.rg ?? ""} onChange={(e) => f.set("rg", e.target.value)} />
                  </Field>
                  <Field label="Órgão emissor" className="col-md-3">
                    <input value={v.rgIssuer ?? ""} onChange={(e) => f.set("rgIssuer", e.target.value)} />
                  </Field>
                  <Field label="Inscrição Municipal" className="col-md-3">
                    <input value={v.im ?? ""} onChange={(e) => f.set("im", e.target.value)} />
                  </Field>
                  <Field label="Próxima visita" error={err.nextVisitAt} className="col-md-3">
                    <input
                      type="date"
                      value={v.nextVisitAt ?? ""}
                      onChange={(e) => f.set("nextVisitAt", e.target.value || null)}
                    />
                  </Field>
                </FormSection>
                <FormSection title="Pessoas de contato" defaultOpen={false}>
                  {(v.persons ?? []).map((p, i) => (
                    <div className="row g-2 align-items-end" key={i}>
                      <Field label="Nome" required error={err[`persons.${i}.name`]} className="col-md-4">
                        <input value={p.name} onChange={(e) => setPerson(i, { name: e.target.value })} />
                      </Field>
                      <Field label="Cargo" className="col-md-3">
                        <input
                          value={p.role ?? ""}
                          onChange={(e) => setPerson(i, { role: e.target.value })}
                        />
                      </Field>
                      <Field label="Telefone" className="col-md-2">
                        <input
                          value={p.phone ?? ""}
                          onChange={(e) => setPerson(i, { phone: e.target.value })}
                        />
                      </Field>
                      <div className="col-md-3 mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          aria-label={`Remover pessoa ${p.name || i + 1}`}
                          onClick={() =>
                            f.set(
                              "persons",
                              (v.persons ?? []).filter((_, j) => j !== i),
                            )
                          }
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => f.set("persons", [...(v.persons ?? []), { name: "" }])}
                    >
                      Adicionar pessoa
                    </button>
                  </div>
                </FormSection>
                <FormSection title="Financeiro" defaultOpen={false}>
                  <Field label="Limite de crédito" className="col-md-4">
                    <select
                      className="form-select"
                      value={v.creditLimitMode}
                      onChange={(e) =>
                        f.set("creditLimitMode", e.target.value as ContactFormValues["creditLimitMode"])
                      }
                    >
                      <option value="unlimited">Ilimitado</option>
                      <option value="zero">Limite zero</option>
                      <option value="value">Valor</option>
                    </select>
                  </Field>
                  {v.creditLimitMode === "value" && (
                    <Field label="Valor do limite" required error={err.creditLimitCents} className="col-md-4">
                      <MoneyInput
                        value={v.creditLimitCents ?? null}
                        onChange={(c) => f.set("creditLimitCents", c)}
                      />
                    </Field>
                  )}
                </FormSection>
                <FormSection title="Observações" defaultOpen={false}>
                  <Field label="Observações" error={err.notes} className="col-12">
                    <textarea
                      rows={4}
                      value={v.notes ?? ""}
                      onChange={(e) => f.set("notes", e.target.value)}
                    />
                  </Field>
                </FormSection>
              </>
            ),
          },
          {
            key: "custom",
            label: "Campos customizados",
            content: (
              <CustomFieldsPanel
                fields={fields.data?.data ?? []}
                values={v.customFields ?? {}}
                onChange={(cf) => f.set("customFields", cf)}
                errors={err}
              />
            ),
          },
          {
            key: "anexos",
            label: "Anexos",
            content: isNew ? (
              <p className="text-muted">Salve o contato para anexar arquivos.</p>
            ) : (
              <AttachmentsPanel
                items={files.data?.data ?? []}
                onUpload={(file) => upload.mutate(file)}
                onDelete={(fid) => delFile.mutate(fid)}
                busy={upload.isPending}
                canUpload={can(perms, "attachments", "create")}
                canDelete={can(perms, "attachments", "delete")}
              />
            ),
          },
        ]}
      />
    </FormPage>
  );
}
